import { useSelector } from 'react-redux';

import { RootState } from '../redux/rootReducer';
import { isStoryCard } from '../redux/cardDbSlice';
import { isMultiAttribute, isTableAttribute } from '../redux/cardAttributeDbSlice';
import useCurrentUserId from './useCurrentUserId';
import { Hash } from '../general';
import { ReferenceType } from '../components/Note';

type CardResObject = { cards: Hash<Object>; cardAttributes: Hash<Object>; memoCardContents: Hash<Object>; uiCardItems: Hash<Object>; notes: Hash<Object>; }
type SheetResObject = CardResObject & { sheets: Hash<Object>; sheetInformations: Hash<Object>; arrows: Hash<Object>; }
type BookResObject = SheetResObject & { books: Hash<Object>; sheetTrees: Hash<Object>; }

const useMakeExportText = (type: ReferenceType, referenceId?: string): [string, string | undefined] => {
  const userId = useCurrentUserId();
  const bookDb = useSelector((state: RootState) => state.bookDb);
  const sheetDb = useSelector((state: RootState) => state.sheetDb);
  const sheetInformationDb = useSelector((state: RootState) => state.sheetInformationDb);
  const sheetTreeDb = useSelector((state: RootState) => state.sheetTreeDb);
  const cardTemplateDb = useSelector((state: RootState) => state.cardTemplateDb);
  const cardDb = useSelector((state: RootState) => state.cardDb);
  const cardAttributeDb = useSelector((state: RootState) => state.cardAttributeDb);
  const memoCardContentDb = useSelector((state: RootState) => state.memoCardContentDb);
  const uiCardItemDb = useSelector((state: RootState) => state.uiCardItemDb);
  const arrowDb = useSelector((state: RootState) => state.arrowDb);
  const noteDb = useSelector((state: RootState) => state.noteDb);

  if (!referenceId || !userId) return ['', undefined];

  const getExtractedBooks = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = bookDb.books[id];
      if (!value) return;
      extractedValues[id] = {
        name: value.name,
        notes: value.notes
      };
    })
    return extractedValues;
  }
  const getExtractedSheets = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = sheetDb.sheets[id];
      if (!value) return;
      extractedValues[id] = {
        name: value.name,
        sheetKind: value.sheetKind
      };
    })
    return extractedValues;
  }
  const getExtractedSheetInformations = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = sheetInformationDb.sheetInformations[id];
      if (!value) return;
      extractedValues[id] = {
        sheetId: value.sheetId,
        name: value.name,
        content: value.content,
        order: value.order
      };
    })
    return extractedValues;
  }
  const getExtractedSheetTrees = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = sheetTreeDb.sheetTrees[id];
      if (!value) return;
      extractedValues[id] = {
        bookId: value.bookId,
        sheetId: value.sheetId,
        order: value.order,
        parentSheetId: value.parentSheetId
      };
    })
    return extractedValues;
  }
  const getExtractedCards = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = cardDb.cards[id];
      if (!value) return;
      if (isStoryCard(value)) {
        extractedValues[id] = {
          sheetId: value.sheetId,
          cardTemplateId: value.cardTemplateId,
          kind: value.kind,
          isGroup: value.isGroup,
          groupParent: value.groupParent,
          orientation: value.orientation,
          links: value.links,
          sheetKind: value.sheetKind,
          order: value.order,
          headerText: value.headerText,
          parentId: value.parentId
        };
      } else {
        extractedValues[id] = {
          sheetId: value.sheetId,
          cardTemplateId: value.cardTemplateId,
          kind: value.kind,
          isGroup: value.isGroup,
          groupParent: value.groupParent,
          orientation: value.orientation,
          links: value.links,
          sheetKind: value.sheetKind
        };
      }
    })
    return extractedValues;
  }
  const getExtractedCardAttributes = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = cardAttributeDb.cardAttributes[id];
      if (!value) return;
      if (isMultiAttribute(value)) {
        extractedValues[id] = {
          languageId: value.languageId,
          name: value.name,
          description: value.description,
          cardAttributeModel: value.cardAttributeModel,
          cardId: value.cardId,
          cardTamplateAttributeId: value.cardTamplateAttributeId,
          content: value.content,
          isMarkdown: value.isMarkdown
        };
      } else if (isTableAttribute(value)) {
        extractedValues[id] = {
          languageId: value.languageId,
          name: value.name,
          description: value.description,
          cardAttributeModel: value.cardAttributeModel,
          cardId: value.cardId,
          cardTamplateAttributeId: value.cardTamplateAttributeId,
          content: value.content,
          columnNames: value.columnNames,
        };
      } else {
        extractedValues[id] = {
          languageId: value.languageId,
          name: value.name,
          description: value.description,
          cardAttributeModel: value.cardAttributeModel,
          cardId: value.cardId,
          cardTamplateAttributeId: value.cardTamplateAttributeId,
          content: value.content
        };
      }
    })
    return extractedValues;
  }
  const getExtractedMemoCardContents = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = memoCardContentDb.memoCardContents[id];
      if (!value) return;
      extractedValues[id] = {
        cardId: value.cardId,
        content: value.content
      };
    })
    return extractedValues;
  }
  const getExtractedUiCardItems = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = uiCardItemDb.uiCardItems[id];
      if (!value) return;
      extractedValues[id] = value;
    })
    return extractedValues;
  }
  const getExtractedArrows = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = arrowDb.arrows[id];
      if (!value) return;
      extractedValues[id] = {
        sheetId: value.sheetId,
        fromId: value.fromId,
        toId: value.toId,
        order: value.order,
        return: value.return,
        links: value.links,
        notes: value.notes
      };
    })
    return extractedValues;
  }
  const getExtractedNotes = (ids: string[]) => {
    const extractedValues: Hash<Object> = {};
    ids.forEach(id => {
      const value = noteDb.notes[id];
      if (!value) return;
      extractedValues[id] = {
        userId: value.userId,
        referenceId: value.referenceId,
        content: value.content
      };
    })
    return extractedValues;
  }

  const makeCardResObject = (targetId: string) => {
    const resObject: CardResObject = { cards: {}, cardAttributes: {}, memoCardContents: {}, uiCardItems: {}, notes: {} };
    const cardId = targetId;
    const attributeIds = Object.keys(cardAttributeDb.cardAttributes).filter(id => cardAttributeDb.cardAttributes[id].cardId === cardId);
    const memoCardContentIds = Object.keys(memoCardContentDb.memoCardContents).filter(id => memoCardContentDb.memoCardContents[id].cardId === cardId);
    const uiCardItemIds = Object.keys(uiCardItemDb.uiCardItems).filter(id => uiCardItemDb.uiCardItems[id].cardId === cardId);
    const noteIds = Object.keys(noteDb.notes).filter(id => noteDb.notes[id].referenceId === cardId && noteDb.notes[id].userId === userId);
    resObject.cards = getExtractedCards([cardId]);
    resObject.cardAttributes = getExtractedCardAttributes(attributeIds);
    resObject.memoCardContents = getExtractedMemoCardContents(memoCardContentIds);
    resObject.uiCardItems = getExtractedUiCardItems(uiCardItemIds);
    resObject.notes = getExtractedNotes(noteIds);
    return resObject;
  }
  const makeSheetResObject = (targetId: string) => {
    const resObject: SheetResObject = { sheets: {}, sheetInformations: {}, cards: {}, cardAttributes: {}, memoCardContents: {}, uiCardItems: {}, arrows: {}, notes: {} };
    const sheetId = targetId;
    const sheetInformationIds = Object.keys(sheetInformationDb.sheetInformations).filter(id => sheetInformationDb.sheetInformations[id].sheetId === sheetId);
    const cardIds = Object.keys(cardDb.cards).filter(id => cardDb.cards[id].sheetId === sheetId);
    const arrowIds = Object.keys(arrowDb.arrows).filter(id => arrowDb.arrows[id].sheetId === sheetId);
    const noteIds = Object.keys(noteDb.notes).filter(id => noteDb.notes[id].referenceId === sheetId && noteDb.notes[id].userId === userId);

    resObject.sheets = getExtractedSheets([sheetId]);
    resObject.sheetInformations = getExtractedSheetInformations(sheetInformationIds);
    resObject.arrows = getExtractedArrows(arrowIds);
    resObject.notes = getExtractedNotes(noteIds);
    cardIds.forEach(id => {
      const cardResObject = makeCardResObject(id);
      Object.assign(resObject.cards, cardResObject.cards);
      Object.assign(resObject.cardAttributes, cardResObject.cardAttributes);
      Object.assign(resObject.memoCardContents, cardResObject.memoCardContents);
      Object.assign(resObject.uiCardItems, cardResObject.uiCardItems);
      Object.assign(resObject.notes, cardResObject.notes);
    })

    return resObject;
  }
  const makeBookResObject = (targetId: string) => {
    const resObject: BookResObject = { books: {}, sheets: {}, sheetInformations: {}, sheetTrees: {}, cards: {}, cardAttributes: {}, memoCardContents: {}, uiCardItems: {}, arrows: {}, notes: {} };
    const bookId = targetId;
    const sheetTreeIds = Object.keys(sheetTreeDb.sheetTrees).filter(id => sheetTreeDb.sheetTrees[id].bookId === bookId);
    const sheetIds = sheetTreeIds.map(id => sheetTreeDb.sheetTrees[id].sheetId);
    const noteIds = Object.keys(noteDb.notes).filter(id => noteDb.notes[id].referenceId === bookId && noteDb.notes[id].userId === userId);

    resObject.books = getExtractedBooks([bookId]);
    resObject.sheetTrees = getExtractedSheetTrees(sheetTreeIds);
    resObject.notes = getExtractedNotes(noteIds);
    sheetIds.forEach(id => {
      const sheetResObject = makeSheetResObject(id);
      Object.assign(resObject.sheets, sheetResObject.sheets);
      Object.assign(resObject.sheetInformations, sheetResObject.sheetInformations);
      Object.assign(resObject.arrows, sheetResObject.arrows);
      Object.assign(resObject.cards, sheetResObject.cards);
      Object.assign(resObject.cardAttributes, sheetResObject.cardAttributes);
      Object.assign(resObject.memoCardContents, sheetResObject.memoCardContents);
      Object.assign(resObject.uiCardItems, sheetResObject.uiCardItems);
      Object.assign(resObject.notes, sheetResObject.notes);
    })

    return resObject;
  }

  if (type === 'card') {
    const resObject = makeCardResObject(referenceId);
    const res = JSON.stringify(resObject);
    const card = cardDb.cards[referenceId];
    if (!card) return [res, undefined];
    let exportName: string | undefined = undefined;
    const originTemplate = cardTemplateDb.cardTemplates[card.cardTemplateId];
    if (originTemplate) exportName = originTemplate.name;
    return [res, exportName];
  } else if (type === 'sheet') {
    const resObject = makeSheetResObject(referenceId);
    const res = JSON.stringify(resObject);
    const sheet = sheetDb.sheets[referenceId];
    if (!sheet) return [res, undefined];
    else return [res, sheet.name];
  } else if (type === 'book') {
    const resObject = makeBookResObject(referenceId);
    const res = JSON.stringify(resObject);
    const book = bookDb.books[referenceId];
    if (!book) return [res, undefined];
    else return [res, book.name];
  }
  else return ['', undefined];
}

export default useMakeExportText;
